import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';
import { CircularProgress, Box } from '@mui/material';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      if (authService.isAuthenticated()) {
        // Verify token and get user data
        const result = await authService.verifyToken();
        if (result.success) {
          setUser(result.user);
        } else {
          // Token is invalid, clear auth data
          authService.logout();
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      // Handle different types of errors gracefully
      if (error.response?.status === 400 && error.response?.data?.message === 'Validation failed') {
        console.log('Token validation failed, clearing auth data');
        authService.logout();
        setUser(null);
      } else if (error.response?.status === 401) {
        console.log('Unauthorized, clearing auth data');
        authService.logout();
        setUser(null);
      } else {
        console.error('Unexpected auth error:', error);
        authService.logout();
        setUser(null);
      }
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  };

  const login = async (credentials) => {
    setLoading(true);
    try {
      const result = await authService.login(credentials);
      if (result.success) {
        setUser(result.user);
        return { success: true };
      } else {
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Login failed. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  const loginWithToken = async (azureToken) => {
    setLoading(true);
    try {
      const result = await authService.loginWithToken(azureToken);
      if (result.success) {
        setUser(result.user);
        return { success: true };
      } else {
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Token login error:', error);
      return { success: false, message: 'Sign in with Microsoft failed. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setLoading(false);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    authService.setUser(userData);
  };

  const value = {
    user,
    loading,
    initialized,
    login,
    loginWithToken,
    logout,
    updateUser,
    isAuthenticated: authService.isAuthenticated(),
    isAdmin: user?.role === 'admin',
    isUser: user?.role === 'admin' || user?.role === 'user',
    hasPermission: (permission) => authService.hasPermission(permission)
  };

  // Show loading spinner while initializing
  if (!initialized) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };